import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react';

import { cn } from '@ui/lib/utils';
import { Button } from '@ui/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/components/ui/table';

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: {
  column: Column<TData, TValue>;
  title: string;
  className?: string;
}) {
  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>;
  }

  const sorted = column.getIsSorted();
  let SortIcon = ArrowUpDown;
  let sortIconClass = 'ml-1 size-4 shrink-0 opacity-60';
  if (sorted === 'desc') {
    SortIcon = ArrowDown;
    sortIconClass = 'ml-1 size-4 shrink-0';
  } else if (sorted === 'asc') {
    SortIcon = ArrowUp;
    sortIconClass = 'ml-1 size-4 shrink-0';
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-ml-2 h-8 px-2 data-[state=open]:bg-accent"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        <span>{title}</span>
        <SortIcon className={sortIconClass} aria-hidden />
      </Button>
    </div>
  );
}

export type DataTableProps<TData> = {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /** Applied on first render; user can change sort afterward */
  initialSorting?: SortingState;
  isLoading?: boolean;
  getRowId?: (originalRow: TData, index: number) => string;
  emptyMessage?: string;
  /** When set, each body row becomes clickable and calls this with the row's original data. */
  onRowClick?: (row: TData) => void;
  /** Extra classes for a body row, from its data — e.g. muting a row whose subject is no longer active. */
  rowClassName?: (row: TData) => string | undefined;
  /** Insets the first/last columns from the table edges so content doesn't sit flush against it. */
  padded?: boolean;
};

export function DataTable<TData>({
  columns,
  data,
  initialSorting = [],
  isLoading = false,
  getRowId,
  emptyMessage = '—',
  onRowClick,
  rowClassName,
  padded = false,
}: DataTableProps<TData>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId,
  });

  return (
    <div className="relative w-full overflow-auto rounded-md border bg-card text-card-foreground shadow-xs max-sm:-mx-4 max-sm:w-auto max-sm:rounded-none max-sm:border-x-0">
      {isLoading ? (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-background/60"
          aria-busy
          aria-label={t('loading')}
        >
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead
                  key={header.id}
                  className={cn(
                    padded && 'first:pl-6 last:pr-6',
                    (header.column.columnDef.meta as { className?: string } | undefined)?.className,
                  )}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map(row => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={cn('group h-11', onRowClick && 'cursor-pointer', rowClassName?.(row.original))}
              >
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      padded && 'first:pl-6 last:pr-6',
                      (cell.column.columnDef.meta as { className?: string } | undefined)?.className,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
